import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CancelConfirmDialogProps {
  open: boolean;
  isLoading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Cancel task confirmation dialog — styled to match Xamarin popup design system.
 *
 * Uses the same button styles as CompletedTaskPopUpMobile:
 *  - Cancel/Keep: border=#003366, bg=transparent, text=#003366 (UnSelectedButton)
 *  - Confirm: bg=#003366, text=white (SelectedButton)
 *  - Frame: CornerRadius=22, bg=#FFFFFF
 */
export function CancelConfirmDialog({
  open,
  isLoading,
  onConfirm,
  onClose,
}: CancelConfirmDialogProps) {
  const { t } = useTranslation();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      {/* Xamarin: Frame bg=#FFFFFF, CornerRadius=22, HasShadow */}
      <div className="w-full max-w-sm bg-white rounded-[22px] shadow-xl p-6">
        <div className="flex flex-col items-center text-center space-y-4">
          <h3 className="text-lg font-poppins font-semibold text-task-details">
            {t('dashboard.cancelTask')}
          </h3>
          <p className="text-xs font-poppins text-task-label">
            {t('dashboard.cancelConfirmMessage')}
          </p>

          {/* Xamarin-style buttons: CornerRadius=5, HeightRequest=40, WidthRequest=157 */}
          <div className="flex gap-4 w-full pt-2">
            {/* Keep Task — Xamarin UnSelectedButton style */}
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 h-10 rounded-[5px] text-xs font-poppins border border-yms-primary text-yms-primary bg-transparent disabled:opacity-50"
            >
              {t('dashboard.keepTask')}
            </button>

            {/* Cancel Task — Xamarin SelectedButton style */}
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 h-10 rounded-[5px] text-xs font-poppins bg-yms-primary text-white disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mx-auto" />
              ) : (
                t('dashboard.cancelTaskButton')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
